import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";
import {
  DeleteLeagueRegistrationFee,
  PostLeagueRegistrationFee,
} from "api/rpc/2024-04/facilityAdmin/league/registration/registration";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";

import { showError } from "redux/actions/ui";
import { ILeagueRegistrationFee } from "redux/reducers/models/league";
import { IProduct, IVariant } from "redux/reducers/models/product";
import { displayCurrency } from "helpers/Helpers";
import useModal from "hooks/modals/useModal";
import { useAppDispatch } from "hooks/redux";
import classNames from "classnames";

import Card from "components/card/Card";
import Popup from "components/popup/Popup";
import Sheet from "components/sheet/Sheet";
import Input from "components/form/input/Input";
import Search from "components/search/Search";
import { Select } from "components/select/index";
import DataTable from "components/dataTable/DataTable";
import Portal from "elements/Portal";
import RegistrationFeeInputs from "./RegistrationFeeInputs";

import "./registrationFeeTab.scss";
import FormLayout from "components/form/FormLayout";

interface ILeagueRegistrationFeeTabProps {
  leagueId: number;
  fees: ILeagueRegistrationFee[];
  addons: ILeagueRegistrationFee[];
  setUpdatedFees: (fees: ILeagueRegistrationFee[], loadTrigger: boolean) => void;
  setUpdatedAddons: (addons: ILeagueRegistrationFee[], loadTrigger: boolean) => void;
}

export default function RegistrationFeeTab(props: ILeagueRegistrationFeeTabProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [products, setProducts] = useState<IProduct[]>(undefined);

  const {
    state: removeFeeModal,
    updateModal: updateRemoveFeeModal,
    closeModal: closeRemoveFeeModal,
  } = useModal({ feeId: null });

  const {
    state: removeAddonModal,
    updateModal: updateRemoveAddonModal,
    closeModal: closeRemoveAddonModal,
  } = useModal({ addonId: null });

  const {
    state: newFeeModal,
    updateModal: updateNewFeeModal,
    closeModal: closeNewFeeModal,
  } = useModal({
    playerCount: 1,
    description: "",
    price: null,
    selectedVariant: undefined as IVariant,
    search: "",
    pageTwo: false,
  });

  const {
    state: newAddOnModal,
    updateModal: updateNewAddOnModal,
    closeModal: closeNewAddOnModal,
  } = useModal({
    playerCount: 1,
    description: "",
    price: null,
    selectedVariant: undefined as IVariant,
    search: "",
    pageTwo: false,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadProducts(newFeeModal.search, source.token);
    return () => source.cancel();
  }, [newFeeModal.search]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadProducts(newAddOnModal.search, source.token);
    return () => source.cancel();
  }, [newAddOnModal.search]);

  async function loadProducts(search?: string, token?: CancelToken) {
    if (products !== undefined) {
      setProducts(undefined);
    }

    const res = await GetProduct({ search: search, extended_variants: true }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading products.")));
    }

    setProducts(res.status !== StatusCode.OK ? [] : res.data);
  }

  /** Close removeFeeModal when successful */
  async function deleteLeagueRegistrationFee(registration_fee_id: string | number) {
    const deleteLeagueRegistrationFeeResponse = await DeleteLeagueRegistrationFee(
      { registration_fee_id: registration_fee_id },
      true,
    );

    if (deleteLeagueRegistrationFeeResponse.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.league.fees.003")));
      return;
    }

    props.setUpdatedFees(
      props.fees.filter(fee => fee.id !== registration_fee_id),
      false,
    );
    closeRemoveFeeModal();
  }

  /** Close removeAddonModal when successful */
  async function deleteLeagueRegistrationAddon(registration_addon_id: string | number) {
    const deleteLeagueRegistrationAddonResponse = await DeleteLeagueRegistrationFee(
      { registration_fee_id: registration_addon_id },
      true,
    );

    if (deleteLeagueRegistrationAddonResponse.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting registration add on.")));
      return;
    }

    props.setUpdatedAddons(
      props.addons.filter(fee => fee.id !== registration_addon_id),
      true,
    );

    closeRemoveAddonModal();
  }

  async function handleAddNewFee() {
    if (newFeeModal.selectedVariant?.id === undefined) {
      dispatch(showError(t("secure.facility.league.league_registration_fees_new.001")));
      return;
    }

    const res = await PostLeagueRegistrationFee(
      {
        type: "registration_fee",
        league_id: props.leagueId,
        variant_id: newFeeModal.selectedVariant.id,
        players: newFeeModal.playerCount,
        description: newFeeModal.description,
        price: newFeeModal.price,
      },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.league.league_registration_fees_new.003")));
      return;
    }

    props.setUpdatedFees(props.fees, true);
    closeNewFeeModal();
  }

  async function handleAddNewAddOn() {
    if (newAddOnModal.selectedVariant?.id === undefined) {
      dispatch(showError(t("No add on is selected")));
      return;
    }

    const res = await PostLeagueRegistrationFee(
      {
        type: "add_on",
        league_id: props.leagueId,
        variant_id: newAddOnModal.selectedVariant.id,
        players: newAddOnModal.playerCount,
        description: newAddOnModal.description,
        price: newAddOnModal.price,
      },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error adding the add on.")));
      return;
    }

    props.setUpdatedFees(props.fees, true);
    closeNewAddOnModal();
  }

  return (
    <>
      <Card
        title={t("Fees")}
        titleActions={[{ content: t("Add Fee"), action: () => updateNewFeeModal({ isOpen: true }) }]}
        style={{ marginTop: "8px" }}
      >
        <Card.Section>
          <DataTable
            columns={[
              { label: t("Title") },
              { label: t("Description") },
              { label: t("Price") },
              { label: t("Players") },
              { label: t("Delete") },
            ]}
            loading={props.fees === undefined}
          >
            {props.fees?.map(fee => (
              <tr key={fee.id}>
                <td>
                  <p>
                    {fee.product_title === fee.variant_title
                      ? fee.product_title
                      : fee.product_title + " - " + fee.variant_title}
                  </p>

                  <p className="text-sm text-subdued">{fee.product.subtitle}</p>
                </td>
                <td>{fee.description}</td>
                <td>{displayCurrency("cad", fee.price ? fee.price : fee.variant.price)}</td>
                <td>{fee.players}</td>
                <td>
                  <FontAwesomeIcon
                    onClick={() => updateRemoveFeeModal({ isOpen: true, feeId: fee.id })}
                    className="text-lg cursor-pointer py-2"
                    icon={["far", "trash"]}
                  />
                </td>
              </tr>
            ))}
          </DataTable>
        </Card.Section>
      </Card>

      <Card
        title={t("Add Ons")}
        titleActions={[{ content: t("Add Add On"), action: () => updateNewAddOnModal({ isOpen: true }) }]}
        style={{ marginTop: "8px" }}
      >
        <Card.Section>
          <DataTable
            columns={[{ label: t("Title") }, { label: t("Description") }, { label: t("Price") }, { label: t("Delete") }]}
            loading={props.addons === undefined}
          >
            {props.addons?.map(addon => (
              <tr key={addon.id}>
                <td>
                  <p>
                    {addon.product_title === addon.variant_title
                      ? addon.product_title
                      : addon.product_title + " - " + addon.variant_title}
                  </p>

                  <p className="text-sm text-subdued">{addon.product.subtitle}</p>
                </td>
                <td>{addon.description}</td>
                <td>{displayCurrency("cad", addon.price ? addon.price : addon.variant.price)}</td>
                <td>
                  <FontAwesomeIcon
                    onClick={() => updateRemoveAddonModal({ isOpen: true, addonId: addon.id })}
                    className="text-lg cursor-pointer py-2"
                    icon={["far", "trash"]}
                  />
                </td>
              </tr>
            ))}
          </DataTable>
        </Card.Section>
      </Card>

      <RegistrationFeeInputs leagueId={props.leagueId} />

      <Portal isMounted={removeFeeModal.isOpen}>
        <Popup
          type="warning"
          open={removeFeeModal.isOpen}
          title={t("Warning")}
          description={t("You are about to remove this booking fee from the league.")}
          closable
          onOk={() => deleteLeagueRegistrationFee(removeFeeModal.feeId)}
          onCancel={closeRemoveFeeModal}
        />
      </Portal>

      <Portal isMounted={removeAddonModal.isOpen}>
        <Popup
          type="warning"
          open={removeAddonModal.isOpen}
          title={t("Warning")}
          description={t("You are about to remove this addon from the league.")}
          closable
          onOk={() => deleteLeagueRegistrationAddon(removeAddonModal.addonId)}
          onCancel={closeRemoveAddonModal}
        />
      </Portal>

      <Portal isMounted={newFeeModal.isOpen}>
        <Sheet
          size="medium"
          open={newFeeModal.isOpen}
          title={t("New Fee")}
          okText={!newFeeModal.pageTwo ? t("Next") : t("Confirm")}
          onOk={!newFeeModal.pageTwo ? null : () => handleAddNewFee()}
          cancelText={!newFeeModal.pageTwo ? t("Cancel") : t("Back")}
          onCancel={
            !newFeeModal.pageTwo
              ? () => closeNewFeeModal()
              : () =>
                  updateNewFeeModal({
                    pageTwo: false,
                    selectedVariant: undefined,
                    playerCount: 1,
                    description: "",
                    price: null,
                  })
          }
          backDropCancel
          okDisabled={newFeeModal.selectedVariant === undefined}
        >
          {/* Initial Page.  Select chosen product */}
          {!newFeeModal.pageTwo ? (
            <>
              <div className="product-search-bar ">
                <Search
                  searchCallback={searchValue => updateNewFeeModal({ search: searchValue })}
                  historyKey="registration-fees-fee"
                />
              </div>
              <DataTable
                columns={[
                  { label: "", width: "50%" },
                  { label: "", width: "30%" },
                  { label: "", width: "20%" },
                ]}
                loading={products === undefined}
                hideHeader
              >
                {products
                  ?.filter(
                    item => !item.variants.every(variant => props.fees?.some(fee => fee.variant_id === variant.id)),
                  )
                  ?.map(item => (
                    <React.Fragment key={item.id}>
                      <tr
                        onClick={
                          item.variant_count === 1
                            ? () => updateNewFeeModal({ selectedVariant: { ...item.variants[0] }, pageTwo: true })
                            : undefined
                        }
                        className={item.variant_count === 1 ? "clickable" : null}
                      >
                        <td>
                          <p>{item.title}</p>
                          {item.subtitle ? <p>{item.subtitle}</p> : null}
                        </td>
                        <td>{item?.vendor_title}</td>
                        <td>{item.type}</td>
                      </tr>
                      {item.variant_count > 1 &&
                        item.variants
                          .filter(variant => !props.fees?.some(fee => fee.variant_id === variant.id))
                          .map(variant => (
                            <tr
                              key={`product_variant_${variant.id}`}
                              onClick={() => updateNewFeeModal({ selectedVariant: { ...variant }, pageTwo: true })}
                              className="clickable"
                            >
                              <td style={{ textIndent: "3rem" }}>{variant.title}</td>
                              <td />
                              <td />
                            </tr>
                          ))}
                    </React.Fragment>
                  ))}
              </DataTable>
            </>
          ) : null}

          {/* Page 2.  Player count & description */}
          {newFeeModal.pageTwo ? (
            <Card>
              <Card.Section title={newFeeModal.selectedVariant?.title}>
                <FormLayout>
                  <FormLayout.Group>
                    <Select
                      label={t("Number of Players")}
                      onChange={(playerCount: number) => updateNewFeeModal({ playerCount: playerCount })}
                      defaultValue={newFeeModal.playerCount}
                    >
                      {[1, 2, 3, 4].map((option, index) => {
                        return (
                          <Select.Option key={index} value={option}>
                            {option}
                          </Select.Option>
                        );
                      })}
                    </Select>
                    <Input
                      id="price"
                      label={t("Price")}
                      value={newFeeModal.price}
                      onChange={e => updateNewFeeModal({ price: Number(e.target.value) })}
                      type="number"
                      prefix={"$"}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Input
                      id="description"
                      label={t("Description")}
                      value={newFeeModal.description}
                      onChange={e => updateNewFeeModal({ description: e.target.value })}
                    />
                  </FormLayout.Group>
                </FormLayout>
              </Card.Section>
            </Card>
          ) : null}
        </Sheet>
      </Portal>

      <Portal isMounted={newAddOnModal.isOpen}>
        <Sheet
          size="medium"
          open={newAddOnModal.isOpen}
          title={t("New Add On")}
          okText={!newAddOnModal.pageTwo ? "Next" : t("Confirm")}
          onOk={!newAddOnModal.pageTwo ? null : () => handleAddNewAddOn()}
          cancelText={!newAddOnModal.pageTwo ? t("Cancel") : "Back"}
          onCancel={
            !newAddOnModal.pageTwo
              ? () => closeNewAddOnModal()
              : () =>
                  updateNewAddOnModal({
                    pageTwo: false,
                    selectedVariant: undefined,
                    playerCount: 1,
                    description: "",
                    price: null,
                  })
          }
          backDropCancel
          okDisabled={newAddOnModal.selectedVariant === undefined}
        >
          {/* Initial Page.  Select chosen product */}
          {!newAddOnModal.pageTwo ? (
            <>
              <div className="fee-product-search-container">
                <div className="product-search-bar">
                  <Search
                    searchCallback={searchValue => updateNewAddOnModal({ search: searchValue })}
                    historyKey="registration-fees-add-on"
                  />
                </div>
              </div>
              <div className="fee-products-container">
                <DataTable
                  columns={[
                    { label: "", width: "50%" },
                    { label: "", width: "30%" },
                    { label: "", width: "20%" },
                  ]}
                  loading={products === undefined}
                  hideHeader
                >
                  {products
                    ?.filter(
                      item =>
                        !item.variants.every(variant => props.addons?.some(addon => addon.variant_id === variant.id)),
                    )
                    ?.map(item => (
                      <React.Fragment key={item.id}>
                        <tr
                          onClick={
                            item.variant_count === 1
                              ? () => updateNewAddOnModal({ selectedVariant: { ...item.variants[0] }, pageTwo: true })
                              : undefined
                          }
                          className={classNames({
                            clickable: item.variant_count === 1,
                            "addon-selected-variant":
                              newAddOnModal.selectedVariant &&
                              newAddOnModal.selectedVariant?.id === item.variants[0]?.id,
                          })}
                        >
                          <td>
                            <p>{item.title}</p>
                            {item.subtitle ? <p>{item.subtitle}</p> : null}
                          </td>
                          <td>{item?.vendor_title}</td>
                          <td>{item.type}</td>
                        </tr>
                        {item.variant_count > 1 &&
                          item.variants
                            .filter(variant => !props.addons?.some(addon => addon.variant_id === variant.id))
                            .map(variant => (
                              <tr
                                key={`product_variant_${variant.id}`}
                                onClick={() => updateNewAddOnModal({ selectedVariant: { ...variant }, pageTwo: true })}
                                className={classNames("clickable", {
                                  "addon-selected-variant":
                                    newAddOnModal.selectedVariant && newAddOnModal.selectedVariant?.id === variant.id,
                                })}
                              >
                                <td style={{ textIndent: "3rem" }}>{variant.title}</td>
                                <td />
                                <td />
                              </tr>
                            ))}
                      </React.Fragment>
                    ))}
                </DataTable>
              </div>
            </>
          ) : null}

          {/* Page 2.  Player count & description */}
          {newAddOnModal.pageTwo ? (
            <Card>
              <Card.Section title={newAddOnModal.selectedVariant?.title}>
                <FormLayout>
                  <FormLayout.Group>
                    <Input
                      id="price"
                      label={t("Price")}
                      value={newAddOnModal.price}
                      onChange={e => updateNewAddOnModal({ price: Number(e.target.value) })}
                      type="number"
                      prefix={"$"}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Input
                      id="description"
                      label={t("Description")}
                      value={newAddOnModal.description}
                      onChange={e => updateNewAddOnModal({ description: e.target.value })}
                    />
                  </FormLayout.Group>
                </FormLayout>
              </Card.Section>
            </Card>
          ) : null}
        </Sheet>
      </Portal>
    </>
  );
}
